import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Volume2, VolumeX } from "lucide-react";

const STORAGE_KEY = "who_intro_seen_v1";
const VIDEO_SRC = "/assets/who-intro.mp4";
const ANIMATED_FALLBACK_MS = 5200;

/**
 * First-visit cinematic intro. Attempts to play the Sora-generated MP4;
 * if it fails/is missing, falls back to an animated SVG brand sequence.
 * Runs once per visitor (localStorage-guarded).
 */
export default function IntroOverlay() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("video"); // "video" | "animated"
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (_) { /* ignore */ }
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  useEffect(() => {
    if (!visible || mode !== "animated") return;
    setReady(true);
    const t = setTimeout(dismiss, ANIMATED_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [visible, mode]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="intro-overlay"
        data-testid="intro-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[100] bg-navy-deep flex items-center justify-center overflow-hidden"
      >
        {mode === "video" && (
          <video
            ref={videoRef}
            data-testid="intro-video"
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted={muted}
            playsInline
            preload="auto"
            onCanPlay={() => setReady(true)}
            onEnded={dismiss}
            onError={() => setMode("animated")}
          >
            <source src="/assets/who-intro.webm" type="video/webm" />
            <source src="/assets/who-intro.mp4" type="video/mp4" />
          </video>
        )}

        {mode === "animated" && <AnimatedSequence />}

        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/40 via-transparent to-navy-deep/80 pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 12 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <div className="font-display text-cream text-6xl md:text-8xl font-black tracking-tight leading-none drop-shadow-2xl">
            WH<span className="text-gold">O</span>
          </div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-gold/70" />
            <span className="text-[11px] tracking-[0.32em] font-bold uppercase text-gold-light">
              Warm Hand Off
            </span>
            <span className="h-px w-10 bg-gold/70" />
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ delay: 2.2, duration: 1 }}
            className="mt-8 font-display text-cream/95 text-2xl md:text-4xl font-medium tracking-tight drop-shadow"
          >
            If not you, then <span className="text-gold">WHO</span>.
          </motion.p>
        </motion.div>

        <div className="absolute top-6 right-6 flex items-center gap-2 z-20">
          {mode === "video" && (
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              data-testid="intro-sound-toggle"
              aria-label={muted ? "Unmute" : "Mute"}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-cream transition-colors border border-white/15"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            data-testid="intro-skip-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-cream text-sm font-semibold transition-colors border border-white/15"
          >
            {mode === "video" ? "Skip intro" : "Enter site"} <X className="w-4 h-4" />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          transition={{ delay: 3.2, duration: 0.6 }}
          className="absolute bottom-10 inset-x-0 text-center text-xs uppercase tracking-[0.3em] text-cream/60 z-10"
        >
          Coordination infrastructure for veterans, families, and the organizations that serve them.
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AnimatedSequence() {
  const nodes = [
    { x: 20, y: 25, delay: 0.5 },
    { x: 78, y: 18, delay: 0.8 },
    { x: 88, y: 55, delay: 1.1 },
    { x: 68, y: 85, delay: 1.4 },
    { x: 25, y: 80, delay: 1.7 },
    { x: 8, y: 55, delay: 2.0 },
  ];
  const center = { x: 50, y: 50 };
  return (
    <div className="absolute inset-0 hero-gradient" aria-hidden>
      <div className="absolute inset-0 opacity-[0.09]" style={{
        backgroundImage: "radial-gradient(rgba(198,147,76,0.5) 1px, transparent 1px)",
        backgroundSize: "36px 36px",
      }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 3, ease: "easeOut" }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(198,147,76,0.22) 0%, rgba(198,147,76,0.06) 40%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
        {nodes.map((n, i) => (
          <motion.line key={`ln-${i}`} x1={center.x} y1={center.y} x2={n.x} y2={n.y}
            stroke="#C6934C" strokeWidth="0.18" strokeDasharray="0.6 0.8" strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.85 }}
            transition={{ duration: 1.4, delay: n.delay + 0.5, ease: "easeInOut" }} />
        ))}
        {nodes.map((n, i) => (
          <motion.g key={`nd-${i}`}>
            <motion.circle cx={n.x} cy={n.y} r="0.9" fill="#DDB27C" opacity="0.35"
              initial={{ scale: 0 }} animate={{ scale: [0, 2.2, 1.6] }}
              transition={{ delay: n.delay, duration: 1.6, ease: "easeOut" }}
              style={{ transformOrigin: `${n.x}px ${n.y}px` }} />
            <motion.circle cx={n.x} cy={n.y} r="0.55" fill="#C6934C"
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: n.delay, duration: 0.5, ease: "backOut" }}
              style={{ transformOrigin: `${n.x}px ${n.y}px` }} />
          </motion.g>
        ))}
        <motion.circle cx={center.x} cy={center.y} r="1.6" fill="#F6F1E6"
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          style={{ transformOrigin: `${center.x}px ${center.y}px` }} />
        <motion.circle cx={center.x} cy={center.y} r="1.6" fill="none" stroke="#DDB27C" strokeWidth="0.15"
          initial={{ scale: 1, opacity: 0.9 }} animate={{ scale: [1, 3.5, 5.5], opacity: [0.9, 0.2, 0] }}
          transition={{ delay: 0.6, duration: 2.4, repeat: 1, ease: "easeOut" }}
          style={{ transformOrigin: `${center.x}px ${center.y}px` }} />
      </svg>
    </div>
  );
}
